import { getEnv, getRoot } from 'mobx-easy';
import {
	DataOptions,
	GetAllDriversOptions,
	KeysetPaginationRequest,
	MappedStorableItem,
	UserGroupValues
} from '@bringg/dashboard-sdk';
import { reaction } from 'mobx';
import { User } from '@bringg/types';

import { RootEnv } from '../root-env';
import { hasFeatureFlag } from '../other/feature-flags';
import { RootStore } from '../root-store-types';
import DriversStore from './drivers-store';

export default class DriversStoreAdapter {
	private readonly api = getEnv<RootEnv>().dashboardSdk.sdk.v2.users;
	private readonly localStore: DriversStore;

	constructor() {
		this.localStore = new DriversStore();
	}

	public afterRootInit() {
		reaction(
			() => getRoot<RootStore>().views.authStore.isLoggedIn,
			isLoggedIn => {
				if (isLoggedIn) {
					this.localStore.afterUserLoggedIn();
				} else {
					this.localStore.afterUserLoggedOut();
				}
			}
		);
	}

	public loadAll = async (request: KeysetPaginationRequest<DataOptions> = {}) => {
		if (DriversStoreAdapter.shouldUseDashboardSdkApi()) {
			return this.api.loadAll(request);
		}

		const { dashboardSdk } = getEnv<RootEnv>();
		const { page, limit } = request;
		return dashboardSdk.sdk.users.getAll(page, limit);
	};

	public fetchAllDrivers = async (options: KeysetPaginationRequest<GetAllDriversOptions> = {}) => {
		if (DriversStoreAdapter.shouldUseDashboardSdkApi()) {
			return this.api.getAllDrivers(options);
		}

		return this.localStore.fetchAll(options);
	};

	public loadAdminsAndDispatchers = async (request: KeysetPaginationRequest<null> = {}) => {
		if (DriversStoreAdapter.shouldUseDashboardSdkApi()) {
			return this.api.getAllAdminsAndDispatchers(request);
		}

		return this.localStore.fetchAdminsAndDispatchers(request);
	};

	public get(id: number) {
		if (DriversStoreAdapter.shouldUseDashboardSdkApi()) {
			return this.api.get(id);
		} else {
			return this.localStore.get(id);
		}
	}

	public load = async (id: number, options?: DataOptions) => {
		if (DriversStoreAdapter.shouldUseDashboardSdkApi()) {
			return this.api.load(id, options);
		}

		return this.localStore.fetch(id);
	};

	public loadMany = async (ids: number[], options?: DataOptions) => {
		if (DriversStoreAdapter.shouldUseDashboardSdkApi()) {
			return this.api.loadMany(ids, options);
		}

		const signal = options?.signal;
		return this.localStore.loadMany(ids, signal !== undefined ? { signal } : undefined);
	};

	public loadByTeam = async (teamId: number) => {
		if (DriversStoreAdapter.shouldUseDashboardSdkApi()) {
			return this.api.loadAllByTeam(teamId);
		}

		return this.localStore.fetchByTeam(teamId);
	};

	public getByTeam = (teamId: number) => {
		if (DriversStoreAdapter.shouldUseDashboardSdkApi()) {
			return this.api.getTeamGroup(teamId);
		}

		return this.localStore.allByTeam(teamId);
	};

	public allByTeamAndCompany = (teamId: number, companyId: number) => {
		const driversByTeam = this.getByTeam(teamId);
		return driversByTeam?.filter(driver => driver.company_id === companyId);
	};

	get all() {
		if (DriversStoreAdapter.shouldUseDashboardSdkApi()) {
			return this.api.getAll();
		}

		return this.localStore.all;
	}

	get onlineDrivers() {
		if (DriversStoreAdapter.shouldUseDashboardSdkApi()) {
			return this.api.getGroup(UserGroupValues.OnlineDrivers);
		}

		return this.localStore.onlineDrivers;
	}

	get onShiftDrivers() {
		if (DriversStoreAdapter.shouldUseDashboardSdkApi()) {
			return this.api.getGroup(UserGroupValues.OnShiftDrivers);
		}

		return this.localStore.onShiftDrivers;
	}

	get admins() {
		if (DriversStoreAdapter.shouldUseDashboardSdkApi()) {
			return this.api.getGroup(UserGroupValues.Admins);
		}

		return this.localStore.admins;
	}

	get allAdminsAndDispatchers() {
		if (DriversStoreAdapter.shouldUseDashboardSdkApi()) {
			return [...this.api.getGroup(UserGroupValues.Admins), ...this.api.getGroup(UserGroupValues.Dispatchers)];
		}

		return this.localStore.allAdminsAndDispatchers;
	}

	public create = async (user: Partial<User>) => {
		if (DriversStoreAdapter.shouldUseDashboardSdkApi()) {
			return this.api.create(user);
		}

		const { dashboardSdk } = getEnv<RootEnv>();

		return dashboardSdk.sdk.users.create(user);
	};

	public delete = async (id: number, teamId: number) => {
		if (DriversStoreAdapter.shouldUseDashboardSdkApi()) {
			return this.api.delete(id);
		}

		return this.localStore.delete(id, teamId);
	};

	public batchDelete = async (ids: number[], teamId: number) => {
		if (DriversStoreAdapter.shouldUseDashboardSdkApi()) {
			return this.api.deleteMany(ids);
		}

		return this.localStore.batchDelete(ids, teamId);
	};

	public onUpdate(callback: (mappedStorableItem: MappedStorableItem<User>) => void) {
		return this.api.onUpdate(callback);
	}

	public onCreate(callback: (mappedStorableItem: MappedStorableItem<User>) => void) {
		return this.api.onCreate(callback);
	}

	public static shouldUseDashboardSdkApi() {
		return hasFeatureFlag(getRoot<RootStore>().data.usersStore.currentUser, 'drivers_v2_api');
	}
}
